<template>
  <button
    :type="type"
    :disabled="disabled || loading"
    :class="buttonClasses"
    :title="tooltip"
    @click="handleClick"
  >
    <span v-if="loading" class="icon-btn__spinner">
      <svg class="spinner-icon" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" />
      </svg>
    </span>
    <i v-else :class="icon"></i>
  </button>
</template>

<script>
export default {
  name: 'IconButton',
  props: {
    type: {
      type: String,
      default: 'button'
    },
    icon: {
      type: String,
      required: true
    },
    variant: {
      type: String,
      default: 'ghost',
      validator: (v) => ['ghost', 'filled', 'outlined', 'tonal'].includes(v)
    },
    color: {
      type: String,
      default: 'default',
      validator: (v) => ['primary', 'success', 'danger', 'warning', 'info', 'default'].includes(v)
    },
    size: {
      type: String,
      default: 'md',
      validator: (v) => ['xs', 'sm', 'md', 'lg'].includes(v)
    },
    rounded: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    tooltip: {
      type: String,
      default: null
    }
  },
  emits: ['click'],
  computed: {
    buttonClasses() {
      return [
        'icon-btn',
        `icon-btn--${this.variant}`,
        `icon-btn--${this.color}`,
        `icon-btn--${this.size}`,
        {
          'icon-btn--rounded': this.rounded,
          'icon-btn--loading': this.loading,
          'icon-btn--disabled': this.disabled
        }
      ]
    }
  },
  methods: {
    handleClick(e) {
      if (!this.disabled && !this.loading) {
        this.$emit('click', e)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@use '../../scss/variables' as *;

.icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all $transition-fast;
  outline: none;
  flex-shrink: 0;

  &:focus-visible {
    box-shadow: 0 0 0 3px $input-focus-ring-color;
  }

  // Sizes (square buttons)
  &--xs {
    width: 28px;
    height: 28px;
    font-size: 12px;
    border-radius: $radius-md;
  }

  &--sm {
    width: 32px;
    height: 32px;
    font-size: 14px;
    border-radius: $radius-md;
  }

  &--md {
    width: 40px;
    height: 40px;
    font-size: 16px;
    border-radius: $radius-lg;
  }

  &--lg {
    width: 48px;
    height: 48px;
    font-size: 20px;
    border-radius: $radius-lg;
  }

  // Ghost variant (subtle background on hover)
  &--ghost {
    background-color: transparent;
    border-color: $color-border-default;

    &.icon-btn--primary {
      color: $color-primary;
      border-color: $color-primary-100;
      &:hover:not(:disabled) { background-color: $color-primary-50; }
    }
    &.icon-btn--success {
      color: $color-success;
      border-color: rgba($color-success, 0.2);
      &:hover:not(:disabled) { background-color: rgba($color-success, 0.1); }
    }
    &.icon-btn--danger {
      color: $color-danger;
      border-color: rgba($color-danger, 0.2);
      &:hover:not(:disabled) { background-color: rgba($color-danger, 0.1); }
    }
    &.icon-btn--warning {
      color: $color-warning;
      border-color: rgba($color-warning, 0.2);
      &:hover:not(:disabled) { background-color: rgba($color-warning, 0.1); }
    }
    &.icon-btn--info {
      color: $color-info;
      border-color: rgba($color-info, 0.2);
      &:hover:not(:disabled) { background-color: rgba($color-info, 0.1); }
    }
    &.icon-btn--default {
      color: $color-text-secondary;
      border-color: $color-border-default;
      &:hover:not(:disabled) {
        background-color: $color-gray-100;
        color: $color-text-primary;
      }
    }
  }

  // Filled variant
  &--filled {
    &.icon-btn--primary {
      background-color: $color-primary;
      color: white;
      border-color: $color-primary-dark;
      &:hover:not(:disabled) { background-color: $color-primary-dark; }
    }
    &.icon-btn--success {
      background-color: $color-success;
      color: white;
      border-color: $color-success-dark;
      &:hover:not(:disabled) { background-color: $color-success-dark; }
    }
    &.icon-btn--danger {
      background-color: $color-danger;
      color: white;
      border-color: $color-danger-dark;
      &:hover:not(:disabled) { background-color: $color-danger-dark; }
    }
    &.icon-btn--warning {
      background-color: $color-warning;
      color: white;
      border-color: $color-warning-dark;
      &:hover:not(:disabled) { background-color: $color-warning-dark; }
    }
    &.icon-btn--info {
      background-color: $color-info;
      color: white;
      border-color: $color-info-dark;
      &:hover:not(:disabled) { background-color: $color-info-dark; }
    }
    &.icon-btn--default {
      background-color: $color-gray-200;
      color: $color-text-primary;
      border-color: $color-gray-300;
      &:hover:not(:disabled) { background-color: $color-gray-300; }
    }
  }

  // Outlined variant
  &--outlined {
    background-color: transparent;
    border: 1px solid currentColor;

    &.icon-btn--primary {
      color: $color-primary;
      &:hover:not(:disabled) { background-color: $color-primary-50; }
    }
    &.icon-btn--success {
      color: $color-success;
      &:hover:not(:disabled) { background-color: rgba($color-success, 0.1); }
    }
    &.icon-btn--danger {
      color: $color-danger;
      &:hover:not(:disabled) { background-color: rgba($color-danger, 0.1); }
    }
    &.icon-btn--warning {
      color: $color-warning;
      &:hover:not(:disabled) { background-color: rgba($color-warning, 0.1); }
    }
    &.icon-btn--info {
      color: $color-info;
      &:hover:not(:disabled) { background-color: rgba($color-info, 0.1); }
    }
    &.icon-btn--default {
      color: $color-text-secondary;
      border-color: $color-border-default;
      &:hover:not(:disabled) {
        background-color: $color-gray-100;
        color: $color-text-primary;
      }
    }
  }

  // Tonal variant (subtle colored background)
  &--tonal {
    &.icon-btn--primary {
      background-color: $color-primary-50;
      color: $color-primary;
      border-color: $color-primary-100;
      &:hover:not(:disabled) { background-color: $color-primary-100; }
    }
    &.icon-btn--success {
      background-color: rgba($color-success, 0.1);
      color: $color-success-dark;
      border-color: rgba($color-success, 0.2);
      &:hover:not(:disabled) { background-color: rgba($color-success, 0.2); }
    }
    &.icon-btn--danger {
      background-color: rgba($color-danger, 0.1);
      color: $color-danger-dark;
      border-color: rgba($color-danger, 0.2);
      &:hover:not(:disabled) { background-color: rgba($color-danger, 0.2); }
    }
    &.icon-btn--warning {
      background-color: rgba($color-warning, 0.1);
      color: $color-warning-dark;
      border-color: rgba($color-warning, 0.2);
      &:hover:not(:disabled) { background-color: rgba($color-warning, 0.2); }
    }
    &.icon-btn--info {
      background-color: rgba($color-info, 0.1);
      color: $color-info-dark;
      border-color: rgba($color-info, 0.2);
      &:hover:not(:disabled) { background-color: rgba($color-info, 0.2); }
    }
    &.icon-btn--default {
      background-color: $color-gray-100;
      color: $color-text-secondary;
      border-color: $color-gray-200;
      &:hover:not(:disabled) {
        background-color: $color-gray-200;
        color: $color-text-primary;
      }
    }
  }

  // Modifiers
  &--rounded {
    border-radius: $radius-full;
  }

  &--disabled,
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &--loading {
    cursor: wait;
  }

  // Spinner
  &__spinner {
    display: flex;
    align-items: center;
    justify-content: center;

    .spinner-icon {
      width: 18px;
      height: 18px;
      animation: spin 1s linear infinite;

      circle {
        stroke-dasharray: 60;
        stroke-dashoffset: 45;
      }
    }
  }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
